import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE } from '../../lib/api';
import { confirmDialog } from '../../utils/dialogs';
import QuestionFormModal from './QuestionFormModal';

export default function QuestionBankModal({ onClose }) {
    const [categories, setCategories] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showAddQuestion, setShowAddQuestion] = useState(false);
    const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
    const [lastFocusedQuestionId, setLastFocusedQuestionId] = useState(null);

    const listRef = useRef(null);

    const isEditingQuestion = editingQuestionIndex !== null && editingQuestionIndex >= 0;
    const currentEditingQuestion = isEditingQuestion ? questions[editingQuestionIndex] : null;

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            fetchQuestions(selectedCategory);
        }
    }, [selectedCategory]);

    useEffect(() => {
        if (editingQuestionIndex !== null) {
            if (!questions[editingQuestionIndex]) {
                if (questions.length === 0) {
                    setEditingQuestionIndex(null);
                } else {
                    const newIndex = Math.min(editingQuestionIndex, questions.length - 1);
                    if (questions[newIndex]) {
                        setEditingQuestionIndex(newIndex);
                        setLastFocusedQuestionId(questions[newIndex].id);
                    } else {
                        setEditingQuestionIndex(null);
                    }
                }
            }
        }
    }, [questions, editingQuestionIndex]);

    useEffect(() => {
        if (isEditingQuestion) return;
        if (!lastFocusedQuestionId) return;
        if (!listRef.current) return;

        const element = listRef.current.querySelector(`#question-item-${lastFocusedQuestionId}`);
        if (element && listRef.current.contains(element)) {
            requestAnimationFrame(() => {
                element.scrollIntoView({ block: 'center' });
            });
        }
    }, [isEditingQuestion, questions, lastFocusedQuestionId]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/api/exams/categories`);
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
                if (data.length > 0) {
                    setSelectedCategory(data[0]);
                }
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setError("Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    };

    const fetchQuestions = async (category) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/api/exams/questions/${encodeURIComponent(category)}`);
            if (response.ok) {
                const data = await response.json();
                setQuestions(data);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            setError("Failed to fetch questions");
        } finally {
            setLoading(false);
        }
    };

    const parseJsonResponse = async (response) => {
        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
            try {
                return await response.json();
            } catch (parseError) {
                console.warn('Failed to parse JSON response:', parseError);
                return null;
            }
        }

        try {
            const text = await response.text();
            if (!text) return null;
            return JSON.parse(text);
        } catch (textParseError) {
            console.warn('Failed to parse text response as JSON:', textParseError);
            return null;
        }
    };

    const addQuestion = async (questionData, options = {}) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/api/exams/questions`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('go2cdl_access_token')}`
                },
                body: JSON.stringify({ ...questionData, category: selectedCategory }),
            });

            if (response.ok) {
                const result = await parseJsonResponse(response);
                const createdQuestion = result?.question;
                if (createdQuestion) {
                    setQuestions(prev => [...prev, createdQuestion]);
                } else {
                    await fetchQuestions(selectedCategory);
                }
                if (!options?.skipSuccessAlert) {
                    alert("Question added successfully!");
                }
                return { success: true, question: createdQuestion };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to add question");
            }
        } catch (error) {
            console.error("Error adding question:", error);
            setError(error.message || "Failed to add question");
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const updateQuestion = async (questionId, questionData, options = {}) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/api/exams/questions/${questionId}`, {
                // Some hosts/WAFs block PUT; backend also supports POST for updates as a fallback.
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('go2cdl_access_token')}`
                },
                body: JSON.stringify({ ...questionData, category: selectedCategory }),
            });

            if (response.ok) {
                const result = await parseJsonResponse(response);
                const updatedQuestion = result?.question;

                setQuestions(prev => prev.map(q => {
                    if (q.id !== questionId) return q;

                    const fallback = {
                        ...q,
                        ...questionData,
                        id: q.id,
                        category: q.category,
                        choices: Array.isArray(questionData.choices) ? questionData.choices : q.choices,
                        correctIndex: typeof questionData.correctIndex === 'number' ? questionData.correctIndex : q.correctIndex,
                        explanation: questionData.explanation ?? q.explanation,
                        questionHighlight: questionData.questionHighlight ?? q.questionHighlight,
                        answerHighlights: Array.isArray(questionData.answerHighlights) ? questionData.answerHighlights : q.answerHighlights,
                        explanationImagePath: questionData.explanationImagePath ?? q.explanationImagePath,
                        explanationImageMimeType: questionData.explanationImageMimeType ?? q.explanationImageMimeType,
                        explanationImageBase64: questionData.explanationImageBase64 ?? q.explanationImageBase64,
                        explanationImageUrl: questionData.explanationImageUrl ?? q.explanationImageUrl,
                    };

                    return updatedQuestion ? { ...q, ...updatedQuestion } : fallback;
                }));
                setLastFocusedQuestionId(questionId);
                if (!options?.skipSuccessAlert) {
                    alert("Question updated successfully!");
                }
                return { success: true, question: updatedQuestion };
            } else {
                let message = `Failed to update question (HTTP ${response.status})`;
                try {
                    const contentType = response.headers.get('content-type') || '';
                    if (contentType.includes('application/json')) {
                        const errorData = await response.json();
                        if (errorData?.error) message = errorData.error;
                    } else {
                        const text = await response.text();
                        if (text) {
                            const firstLine = text.split('\n')[0]?.trim();
                            message = firstLine ? `${message}: ${firstLine}` : message;
                        }
                    }
                } catch (_err) {
                    // swallow parse errors so we never show "Unexpected token <"
                }
                throw new Error(message);
            }
        } catch (error) {
            console.error("Error updating question:", error);
            setError(error.message || "Failed to update question");
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const deleteQuestion = async (questionId) => {
        if (!confirmDialog("Are you sure you want to delete this question?")) return;

        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/api/exams/questions/${questionId}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem('go2cdl_access_token')}`
                }
            });

            if (response.ok) {
                setQuestions(prev => prev.filter(q => q.id !== questionId));
                alert("Question deleted successfully!");
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to delete question");
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            setError(error.message || "Failed to delete question");
        } finally {
            setLoading(false);
        }
    };

    const openQuestionForEdit = (index) => {
        const target = questions[index];
        if (!target) return;
        setLastFocusedQuestionId(target.id);
        setEditingQuestionIndex(index);
    };

    const handleCloseQuestionModal = () => {
        setShowAddQuestion(false);
        setEditingQuestionIndex(null);
    };

    const navigateEditingQuestion = (direction) => {
        if (!isEditingQuestion) return;
        setEditingQuestionIndex((prev) => {
            if (prev === null) return prev;
            const delta = direction === 'prev' ? -1 : 1;
            const nextIndex = prev + delta;
            if (nextIndex < 0 || nextIndex >= questions.length) {
                return prev;
            }
            const nextQuestion = questions[nextIndex];
            if (nextQuestion) {
                setLastFocusedQuestionId(nextQuestion.id);
            }
            return nextIndex;
        });
    };

    const handleQuestionRecordUpdated = useCallback((updatedQuestion) => {
        if (!updatedQuestion || typeof updatedQuestion.id !== 'number') return;
        setQuestions(prev => prev.map(q => (q.id === updatedQuestion.id ? { ...q, ...updatedQuestion } : q)));
    }, []);

    const handleSaveQuestion = async (formData, options = {}) => {
        if (isEditingQuestion && currentEditingQuestion) {
            return updateQuestion(currentEditingQuestion.id, formData, options);
        }
        return addQuestion(formData, options);
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 pt-20">
            <div ref={listRef} className="w-full max-w-6xl rounded-2xl bg-white p-6 shadow-xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-semibold">Question Bank Management</h3>
                    <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                </div>

                {/* Question Management */}
                <div>
                    {/* Category Selector */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-2">Select Category:</label>
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full max-w-xs rounded-lg border px-3 py-2"
                        >
                            {categories.map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>

                    {/* Add Question Button */}
                    <div className="mb-6">
                        <button
                            onClick={() => setShowAddQuestion(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                            + Add New Question
                        </button>
                    </div>

                    {/* Questions List */}
                    <div className="space-y-4">
                        {loading ? (
                            <div className="text-center py-8 text-slate-500">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400 mr-2"></div>
                                Loading questions...
                            </div>
                        ) : questions.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                No questions found for this category.
                            </div>
                        ) : (
                            questions.map((question, index) => (
                                <div
                                    key={question.id || index}
                                    id={question.id ? `question-item-${question.id}` : undefined}
                                    className="border rounded-lg p-4"
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-medium">Question {index + 1}</h4>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openQuestionForEdit(index)}
                                                className="text-blue-600 hover:text-blue-800 text-sm"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => deleteQuestion(question.id)}
                                                className="text-red-600 hover:text-red-800 text-sm"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-sm text-slate-600 mb-2">{question.text}</p>
                                    <div className="text-xs text-slate-500">
                                        <span className="mr-4">Correct: {question.correctIndex + 1}</span>
                                        <span>Category: {question.category || selectedCategory}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Loading Indicator */}
                {loading && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 text-center">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Loading...
                    </div>
                )}

                {/* Error Display */}
                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
                        {error}
                    </div>
                )}

                {/* Add/Edit Question Modal */}
                {(showAddQuestion || isEditingQuestion) && (
                    <QuestionFormModal
                        question={currentEditingQuestion}
                        onSave={handleSaveQuestion}
                        onClose={handleCloseQuestionModal}
                        category={selectedCategory}
                        onNavigate={isEditingQuestion ? navigateEditingQuestion : undefined}
                        hasPrevious={Boolean(isEditingQuestion && editingQuestionIndex > 0)}
                        hasNext={Boolean(isEditingQuestion && editingQuestionIndex < questions.length - 1)}
                        currentIndex={isEditingQuestion ? editingQuestionIndex : null}
                        totalCount={questions.length}
                        isEditing={isEditingQuestion}
                        onQuestionUpdated={handleQuestionRecordUpdated}
                    />
                )}
            </div>
        </div>
    );
}
